import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import 'tom-select/dist/css/tom-select.css'

export default class extends Controller {
  static targets = ["address", "zip", "city", "country"]
  static values = { 
    searchUrl: String
  }

  connect() {
    this.initializeAddressAutocomplete()
  }

  initializeAddressAutocomplete() {
    if (!this.hasAddressTarget) return

    this.tomSelect = new TomSelect(this.addressTarget, {
      valueField: 'address',
      labelField: 'display_name',
      searchField: ['display_name'],
      placeholder: "Adresse eingeben...",
      openOnFocus: true,
      preload: false,
      closeAfterSelect: true,
      selectOnTab: true,
      maxOptions: 10,
      create: true,
      createOnBlur: true,
      createFilter: (input) => {
        return input.length >= 2
      },
      load: (query, callback) => {
        if (!query.length || query.length < 3) return callback()
        this.searchAddresses(query, callback)
      },
      render: {
        option: (item, escape) => {
          if (item.isNew) {
            return `<div class="option text-green-600">
              <span class="title text-sm font-medium">${escape(item.text)}</span>
              <span class="subtext text-xs text-green-500">Manuelle Eingabe</span>
            </div>`
          } else {
            return `<div class="option">
              <span class="title text-sm font-medium">${escape(item.display_name)}</span>
              <span class="subtext text-xs text-gray-500">${escape(item.type || 'Adresse')}</span>
            </div>`
          }
        },
        item: (item, escape) => {
          if (item.isNew) {
            return `<div class="item text-sm text-green-600">${escape(item.text)}</div>`
          } else {
            return `<div class="item text-sm">${escape(item.address || item.display_name)}</div>`
          }
        },
        no_results: (data, escape) => {
          return '<div class="no-results text-sm text-gray-500 py-2">Keine Ergebnisse für "' + escape(data.input) + '" gefunden - manuell eingeben möglich</div>'
        }
      },
      onChange: (value) => {
        this.handleAddressSelection(value)
      }
    })
  }

  async searchAddresses(query, callback) {
    const url = this.searchUrlValue.replace('QUERY', encodeURIComponent(query))

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        const options = data.map(item => ({
          id: item.display_name,
          display_name: item.display_name,
          address: item.address,
          zip: item.zip,
          city: item.city,
          country: item.country,
          country_code: item.country_code,
          latitude: item.latitude,
          longitude: item.longitude,
          type: item.type
        }))
        
        callback(options)
      } else {
        callback()
      }
    } catch (error) {
      console.error('Address search error:', error)
      callback()
    }
  }

  handleAddressSelection(selectedAddress) {
    if (!selectedAddress) return

    const selectedOption = this.tomSelect.options[selectedAddress]
    if (!selectedOption) return

    // Wenn manuelle Eingabe, nur das Adressfeld setzen
    if (selectedOption.isNew) {
      // Manuelle Eingabe - nur Adresse setzen, andere Felder leer lassen
      if (this.hasAddressTarget) {
        this.addressTarget.value = selectedAddress
      }
    } else {
      // Nominatim-Ergebnis - alle Felder automatisch ausfüllen
      if (this.hasAddressTarget && selectedOption.address) {
        this.addressTarget.value = selectedOption.address
      }

      // Automatisch die anderen Felder ausfüllen
      if (this.hasZipTarget && selectedOption.zip) {
        this.zipTarget.value = selectedOption.zip
      }
      
      if (this.hasCityTarget && selectedOption.city) {
        this.cityTarget.value = selectedOption.city
      }
      
      if (this.hasCountryTarget && selectedOption.country_code) {
        // Automatisch das Land basierend auf dem Country Code auswählen
        this.selectCountryByCode(selectedOption.country_code)
      }
    }

    // Trigger change events für andere Controller
    this.addressTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.zipTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.cityTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.countryTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  selectCountryByCode(countryCode) {
    if (!this.hasCountryTarget) return

    // Mapping von Country Codes zu den verfügbaren Optionen
    const countryMapping = {
      'DE': 'DE',
      'AT': 'AT', 
      'CH': 'CH',
      'US': 'US',
      'FR': 'FR',
      'IT': 'IT',
      'ES': 'ES',
      'NL': 'NL',
      'BE': 'BE',
      'LU': 'LU',
      'DK': 'DK',
      'SE': 'SE',
      'NO': 'NO',
      'FI': 'FI',
      'PL': 'PL',
      'CZ': 'CZ',
      'HU': 'HU',
      'SK': 'SK',
      'SI': 'SI',
      'HR': 'HR',
      'RO': 'RO',
      'BG': 'BG',
      'GR': 'GR',
      'CY': 'CY',
      'MT': 'MT',
      'IE': 'IE',
      'PT': 'PT'
    }

    const mappedCode = countryMapping[countryCode] || 'DE' // Fallback zu Deutschland
    
    // Setze den Wert im Select
    this.countryTarget.value = mappedCode
    
    // Trigger change event
    this.countryTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
